import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search, ArrowUp, ArrowDown, ChevronsUpDown, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * MasterTable — reusable data table used across all Master modules.
 * Provides: search · column sort · client-side pagination (10/25/50/100) · total-records footer.
 *
 * Props:
 *   columns    [{ key, label, render?, sortable?, className? }]
 *   rows       array of row objects
 *   searchKeys ["field1","field2",…] — fields used by the search bar
 *   getRowKey  fn(row) → unique id
 *   emptyText
 *   testidPrefix
 *   defaultSort { key, dir: "asc"|"desc" }
 */
export default function MasterTable({
  columns,
  rows,
  searchKeys = [],
  getRowKey = (r) => r.id,
  emptyText = "No records.",
  testidPrefix = "master",
  defaultSort = null,
  actions = null,   // optional element for the toolbar right side
}) {
  const [q, setQ] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(defaultSort);

  const filtered = useMemo(() => {
    let list = rows || [];
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((r) => searchKeys.some((k) =>
        String(r[k] ?? "").toLowerCase().includes(s)));
    }
    if (sort?.key) {
      list = [...list].sort((a, b) => {
        const av = a[sort.key] ?? ""; const bv = b[sort.key] ?? "";
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sort.dir === "desc" ? -cmp : cmp;
      });
    }
    return list;
  }, [rows, q, sort, searchKeys]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const p = Math.min(page, pageCount);
  const pageRows = filtered.slice((p - 1) * pageSize, p * pageSize);

  const toggleSort = (key) => {
    if (!key) return;
    setSort((s) => s?.key === key
      ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "asc" });
    setPage(1);
  };

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-table-wrap`}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid={`${testidPrefix}-search`}
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search…"
            className="pl-8 rounded-md h-9"
          />
        </div>
        {actions}
      </div>

      <div className="border border-border rounded-md overflow-x-auto bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border overline text-left bg-muted/30">
              {columns.map((c) => (
                <th key={c.key}
                    onClick={() => c.sortable !== false && toggleSort(c.key)}
                    data-testid={`${testidPrefix}-th-${c.key}`}
                    className={`p-2 px-3 select-none ${c.className || ""} ${c.sortable !== false ? "cursor-pointer" : ""}`}>
                  <div className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable !== false && (
                      sort?.key === c.key
                        ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)
                        : <ChevronsUpDown size={11} className="opacity-40" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody data-testid={`${testidPrefix}-tbody`}>
            {pageRows.length === 0 && (
              <tr><td colSpan={columns.length} className="p-6 text-center text-xs text-muted-foreground">{emptyText}</td></tr>
            )}
            {pageRows.map((r) => (
              <tr key={getRowKey(r)} className="border-b border-border hover:bg-muted/40"
                  data-testid={`${testidPrefix}-row-${getRowKey(r)}`}>
                {columns.map((c) => (
                  <td key={c.key} className={`p-2 px-3 ${c.className || ""}`}>
                    {c.render ? c.render(r) : (r[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer — total records + page size + pager */}
      <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-muted-foreground">
        <div data-testid={`${testidPrefix}-total`}>
          Total <b className="text-foreground mono">{total}</b>{" "}
          {total > 0 && (
            <>· showing <b className="text-foreground mono">{(p - 1) * pageSize + 1}-{Math.min(p * pageSize, total)}</b></>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1">
            Rows
            <select
              data-testid={`${testidPrefix}-page-size`}
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
              className="h-8 rounded-md border border-input bg-background px-2"
            >
              {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={p <= 1}
            data-testid={`${testidPrefix}-prev`}
            className="border border-input rounded-md h-8 w-8 flex items-center justify-center disabled:opacity-40"
          ><ChevronLeft size={13} /></button>
          <span className="mono">{p} / {pageCount}</span>
          <button
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={p >= pageCount}
            data-testid={`${testidPrefix}-next`}
            className="border border-input rounded-md h-8 w-8 flex items-center justify-center disabled:opacity-40"
          ><ChevronRight size={13} /></button>
        </div>
      </div>
    </div>
  );
}
